import * as React from "react";
import Truncate from "react-truncate";
import { debounce } from "ts-debounce";

const Entities = require("html-entities").XmlEntities;

export interface Recommendation {
	thumbnailURL?: string;
	imageURL?: string;
}

export type ClickEvent = (cardData: CardType) => void;
export type ClickEventProp = (
	e: React.MouseEvent<HTMLDivElement, MouseEvent>
) => void;

export interface CardType {
	title?: string;
	textPrefix?: string;
	text?: string;
	imageURL?: string;
	thumbnailURL?: string;
	positiveReviewsCount?: number;
	link: string;
	linkText: string;
	id: number | string;
	cardNumber: number;
	cardClickHandler: ClickEvent;
}

export interface CardProps {
	data?: CardType;
	loading?: boolean;
	cardClickHandler?: ClickEventProp;
}

type ImageFillMethod =
	| "unknown"
	| "fit-width"
	| "fit-height"
	| "fit"
	| "normal";

interface CardState {
	fillMethod: ImageFillMethod;
}

class Card extends React.Component<CardProps, CardState> {
	static displayName = "Card";
	entities: any;
	imageContainerRef: React.RefObject<HTMLDivElement>;
	imageRef: React.RefObject<HTMLImageElement>;
	_debouncedUpdateImageFillMode: () => void;

	constructor(props: CardProps) {
		super(props);
		this.entities = new Entities();
		this.state = {
			fillMethod: "unknown",
		};
		this.imageContainerRef = React.createRef();
		this.imageRef = React.createRef();
		this._debouncedUpdateImageFillMode = debounce(
			this.updateImageFillMode.bind(this),
			200
		);
	}

	render() {
		const { props, state } = this;

		let dataMarkup = props.children;

		if (props.data) {
			const { data } = props;
			const hasThumbnail = data.thumbnailURL;
			const hasImage = data.imageURL;
			const hasText = data.text;
			const decodedText = this.entities.decode(data.text);
			const hasTitle = data.title;
			let maxLines = 8;
			let thumbnailWidth = 312;
			let thumbnailHeight = 173;
			let thumbnailUrl = null;
			let imageUrl = data.imageURL;

			if (hasImage) {
				if (!hasThumbnail) {
					if (
						decodeURIComponent(imageUrl).match(
							/media\/([a-z0-9]{3})\/([a-z0-9]*)\/([a-z0-9-]*)\//
						)
					) {
						imageUrl = decodeURIComponent(imageUrl).replace(
							/media\/([a-z0-9]{3})\/([a-z0-9]*)\/([a-z0-9-]*)\//,
							"media/$1/s640x480/$3/"
						);
					} else {
						imageUrl = decodeURIComponent(imageUrl).replace(
							/media\/([a-z0-9]{3})\//,
							"media/$1/s640x480/"
						);
					}
				}
			}

			if (hasThumbnail) {
				thumbnailUrl = data.thumbnailURL.replace(/\/$/, "");
			}

			if (hasText) {
				thumbnailHeight = 120;
			}

			if (hasText) {
				if (hasThumbnail) {
					maxLines = 2;
				} else if (hasImage) {
					maxLines = 3;
				}
			}
			if (!hasTitle) {
				maxLines++;
			}

			dataMarkup = (
				<>
					{data.title && (
						<div data-test="cshp-recommendation-card-title" className="title">
							{data.title}
						</div>
					)}
					{hasText && (
						<div data-test="cshp-recommendation-card-body" className="body">
							<div className="text">
								<Truncate lines={maxLines}>{`${this.props.data.textPrefix ||
									""}${decodedText}`}</Truncate>
							</div>
						</div>
					)}
					{hasThumbnail ? (
						<div
							data-test="cshp-recommendation-card-video-thumbnail"
							className="thumbnail"
						>
							<img
								className={hasText ? null : "fit-width"}
								src={`${thumbnailUrl}/width/${thumbnailWidth}/height/${thumbnailHeight}`}
								alt="Video thumbnail"
							/>
							<div className="video-card-play"></div>
						</div>
					) : (
						hasImage && (
							<div
								data-test="cshp-recommendation-card-image"
								ref={this.imageContainerRef}
								className="image"
							>
								<img
									ref={this.imageRef}
									className={state.fillMethod}
									onLoad={e => this.updateImageFillMode()}
									src={imageUrl}
									alt="Solution Image"
								/>
							</div>
						)
					)}
					<div data-test="cshp-recommendation-card-footer" className="footer">
						{data.positiveReviewsCount ? (
							<div className="rating-container">
								<div className="thumbs-background"></div>
								<div
									data-test="cshp-recommendation-card-thumbsup"
									className="thumbsup"
								>
									{data.positiveReviewsCount}
								</div>
							</div>
						) : null}
						<a
							data-test="cshp-recommendation-card-answer-link"
							aria-label={`answer to question ${data.cardNumber}`}
							className="csrec-link answer-link"
							href={data.link}
							onClick={e => e.preventDefault()}
						>
							<span>{data.linkText}</span>
						</a>
					</div>
				</>
			);
		}

		return (
			<div
				className={`csrec-card ${props.loading ? "csrec-card-loading" : ""}`}
				onClick={
					props.data
						? e => props.data.cardClickHandler(props.data)
						: e => props.cardClickHandler(e)
				}
			>
				{dataMarkup}
			</div>
		);
	}

	updateImageFillMode() {
		if (
			this.props.data &&
			this.props.data.imageURL &&
			!this.props.data.thumbnailURL
		) {
			const fillMethod = this.calculateFillMethod();
			this.setState({
				fillMethod,
			});
		}
	}

	getImageContainerRef() {
		return this.imageContainerRef.current;
	}

	getImageRef() {
		return this.imageRef.current;
	}

	calculateFillMethod(): ImageFillMethod {
		const image = this.getImageRef();
		const container = this.getImageContainerRef();
		const imageWidth = image.naturalWidth;
		const imageHeight = image.naturalHeight;
		const aspectRatio = imageWidth / imageHeight;
		const containerWidth = container.clientWidth;
		const containerHeight = container.clientHeight;
		const containerAspectRatio = containerWidth / containerHeight;
		let fillMethod: ImageFillMethod = "normal";
		if (
			imageWidth >= containerWidth &&
			imageHeight > containerHeight &&
			aspectRatio < containerAspectRatio
		) {
			fillMethod = "fit-width";
		} else if (
			imageWidth > containerWidth &&
			imageHeight >= containerHeight &&
			aspectRatio > containerAspectRatio
		) {
			fillMethod = "fit-height";
		} else if (
			imageWidth > containerWidth &&
			imageHeight > containerHeight &&
			aspectRatio === containerAspectRatio
		) {
			fillMethod = "fit";
		}
		return fillMethod;
	}

	componentDidMount() {
		if (typeof window !== "undefined") {
			window.addEventListener("resize", this._debouncedUpdateImageFillMode);
		}
	}

	componentWillUnmount() {
		if (typeof window !== "undefined") {
			window.removeEventListener("resize", this._debouncedUpdateImageFillMode);
		}
	}
}

export default Card;
